import { PayrollTableDataFetch } from "components";
import {
  ViewingRecords,
  Pagination,
  TableContainer,
  BulkDeleteButton,
  TableSearch,
  EditDeleteButtons,
  PayrunSearch,
} from "components/common";
import { reduce, keyBy, keys, isFunction } from "lodash";
import shortid from "shortid";
import React, { useState, useRef, useMemo } from "react";
import { PayrollClient } from "services";
import { Trans } from "react-i18next";

const createComponents = (ref) => () => {
  return {
    Pagination: (props) => {
      const { limit, offset, setOffset, data } = ref.current;

      return (
        <Pagination
          size="sm"
          limit={limit}
          offset={offset}
          onOffsetChanged={setOffset}
          onLimitChanged={(limit)=>{ref.current.setLimit(limit); ref.current.setOffset(0)}}
          count={data.count}
          {...props}
        />
      );
    },

    ViewingRecords: (props) => {
      const { offset, limit, data } = ref.current;

      return (
        <ViewingRecords
          offset={offset}
          count={data.count}
          limit={limit}
          {...props}
        />
      );
    },

    TableContainer: (props) => {
      const { data, ordering, setOrdering, selected, setSelected } =
        ref.current;

      return (
        <TableContainer
          items={data.results}
          ordering={ordering}
          onOrderingChanged={setOrdering}
          selected={selected}
          onSelectedChanged={setSelected}
          {...props}
        />
      );
    },

    PayrollTableDataFetch: (props) => {
      const {
        ordering,
        refreshToken,
        limit,
        search,
        url,
        filters,
        offset,
        setData,
      } = ref.current;

      return (
        <PayrollTableDataFetch
          ordering={ordering}
          refreshToken={refreshToken}
          limit={limit}
          search={search || ''}
          url={url}
          filters={filters}
          offset={offset}
          onDataFetched={setData}
          {...props}
        />
      );
    },

    Loader: () => {
      const { data } = ref.current;
      return data.loading ? (
        <tr>
          <td colSpan="100" className="text-center">
            <span className="fa fa-spinner fa-spin mt-2" /> <Trans>Loading...</Trans>
          </td>
        </tr>
      ) : null;
    },

    TableSearch: (props) => {
      const { setSearch, search } = ref.current;

      return <TableSearch onChange={setSearch} value={search} {...props} />;
    },

    PayrunSearch: (props) => {
      const { setSearch, search } = ref.current;

      return <PayrunSearch onChange={setSearch} value={search} {...props} />;
    },

    EditDeleteButtons: (props) => {
      const { match, onDeleteItem } = ref.current;
      return (
        <EditDeleteButtons url={match.url} onDelete={onDeleteItem} {...props} />
      );
    },

    BulkDeleteButton: (props) => {
      const { toggleDeleteModal, selected, onDeleted, isDeleteModalOpen, url } =
        ref.current;

      const localProps = {
        toggleModal: toggleDeleteModal,
        selected,
        url,
        onDeleted,
        isModalOpen: isDeleteModalOpen,
      };

      return <BulkDeleteButton  {...localProps} {...props} />;
    },
    BulkDelet: (props) => {
      const { toggleDeleteModal, selected, onDeleted, isDeleteModalOpen, url } =
        ref.current;

      const localProps = {
        toggleModal: toggleDeleteModal,
        selected,
        url,
        onDeleted,
        isModalOpen: isDeleteModalOpen,
        // client,
      };

      return <BulkDeleteButton client={PayrollClient()} {...localProps} {...props} />;
    },

    PayrollBulkDeleteButton: (props) => {
      return <BulkDeleteButton client={PayrollClient()} {...props} />;
    },
  };
};

export const useTable = ({
  idKey = "id",
  url,
  match,
  filters: initialFilters = {},
  limit: initialLimit = 50,
} = {}) => {
  const [data, _setData] = useState({ count: 0, results: [] });
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(initialLimit);
  const [filters, setFilters] = useState(initialFilters);
  const [search, setSearch] = useState(null);
  const [selected, _setSelected] = useState({});
  const [ordering, setOrdering] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [refreshToken, setRefreshToken] = useState(null);
  const [numSelected, setNumSelected] = useState(0);

  const state = {
    data,
    offset,
    limit,
    filters,
    numSelected,
    search,
    selected,
    ordering,
    isDeleteModalOpen,
    refreshToken,
  };

  const handlers = {
    setOffset,
    setLimit,
    setFilters,
    setOrdering,
    setSearch,
    setData: (data) => {
      const results = keyBy(data.results, idKey);

      const newSelected = reduce(
        selected,
        (acc, value, key) => {
          if (results[key]) acc[key] = true;
          return acc;
        },
        {}
      );

      _setData(data);
      _setSelected(newSelected);
    },
    mergeFilters: (x) => {
      setFilters({
        ...filters,
        ...x,
      });
    },
    setSelected: (selected) => {
      _setSelected(selected);
      setNumSelected(keys(selected).length);
    },
    updateResults: (arg) => {
      let results = isFunction(arg) ? arg(data.results) : arg;

      _setData({
        ...data,
        results,
      });
    },
    toggleDeleteModal: () => setIsDeleteModalOpen(!isDeleteModalOpen),
    onDeleted: () => setRefreshToken(shortid()),
    refreshData: () => setRefreshToken(shortid()),
    onDeleteItem: (id) => {
      setIsDeleteModalOpen(true);
      _setSelected({ [id]: true });
    },
  };

  const result = {
    ...state,
    ...handlers,
    url,
    match,
  };

  const resultRef = useRef();
  resultRef.current = result;
  const components = useMemo(createComponents(resultRef), []);

  return {
    ...result,
    ...components,
  };
};

export default useTable;
